import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";

const FIRST_ROTATE_DELAY_MS = 4000; // หน่วงก่อนเริ่มสไลด์ เพื่อคง LCP ของภาพแรก

/* โหมด fitWidth (ใช้ในหน้า catalog) — กว้างเท่าหนังสือ แต่สูงไม่เกิน FIT_MAX_H
   ภาพจริงวางแบบ contain ไม่ถูกตัด/ไม่ถูกขยายจนแตก ส่วนที่เหลือเติมด้วยภาพเดียวกันแบบเบลอ
   FIT_RATIO    สัดส่วนไฟล์แบนเนอร์จริง (1080 × 807) ใช้จองพื้นที่กันหน้ากระโดดตอนรูปโหลด
   FIT_MAX_H    เพดานความสูง เพื่อให้ยังเห็นหนังสือในหน้าจอแรก */
const FIT_RATIO = "1080 / 807";
const FIT_MAX_H = "40vh";

/* fitWidth: ความกว้าง (px) ที่ต้องการให้ตรงกับเนื้อหาด้านล่าง — ไม่ส่งมา = แสดงแบบเดิม */
const BannerSection = ({ sections, fitWidth }) => {
  const { authToken, userId } = useLiffProfile();
  const { t, i18n } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // สกัด banner list
  useEffect(() => {
    const fetchData = async () => {
      try {
        const bannerSection = sections?.find(
          (section) => section.type?.toLowerCase() === "banner"
        );

        if (bannerSection && Array.isArray(bannerSection.items)) {
          const bannersData = bannerSection.items
            .map((item) => ({
              image: item.image,
              targetURI: item.targetURI,
              name: item.name || "", // เผื่อยิง analytics ชื่อ
            }))
            .filter((b) => !!b.image);

          setBanners(bannersData);
        } else {
          throw new Error("No banner images found");
        }
      } catch (err) {
        console.error("Error fetching banners:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sections]);

  // === LCP helpers: preconnect + preload เฉพาะภาพแรก ===
  const firstImageUrl = banners[0]?.image;

  useEffect(() => {
    if (!firstImageUrl) return;

    // preconnect โดเมนรูป
    try {
      const u = new URL(firstImageUrl);
      const preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = `${u.protocol}//${u.host}`;
      preconnect.crossOrigin = "";
      document.head.appendChild(preconnect);

      // preload รูปแรก
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.href = firstImageUrl;
      // ถ้าทราบชนิดไฟล์ชัดเจน สามารถกำหนด preload.type = "image/avif" หรือ "image/webp"
      document.head.appendChild(preload);

      // warm-up ด้วย decode (ไม่บล็อค)
      const img = new Image();
      img.src = firstImageUrl;
      img.decoding = "async";
      img.fetchPriority = "high";
      img.decode?.().catch(() => {});

      return () => {
        document.head.removeChild(preconnect);
        document.head.removeChild(preload);
      };
    } catch {
      // no-op
    }
  }, [firstImageUrl]);

  // Auto-rotate: หน่วงรอบแรกเล็กน้อยให้ LCP ของภาพแรกไม่ถูกเปลี่ยนเร็วเกินไป
  useEffect(() => {
    if (banners.length === 0) return;

    // เคลียร์ของเดิมก่อน
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 3000);
    }, FIRST_ROTATE_DELAY_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [banners]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);

    // reset auto-rotate
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
  };

  const isFit = typeof fitWidth === "number" && fitWidth > 0;

  const renderImage = (banner, index) => {
    const isFirst = index === 0;
    const alt = banner.name ? `Banner: ${banner.name}` : `Banner ${index + 1}`;

    if (!isFit) {
      return (
        <img
          className="w-full max-w-[800px] object-cover"
          src={banner.image}
          alt={alt}
          // จุดสำคัญช่วย LCP:
          loading={isFirst ? "eager" : "lazy"}
          fetchpriority={isFirst ? "high" : "auto"}
          decoding="async"
          // แนะนำ: ถ้ารู้สัดส่วนแน่นอน ให้เปิดอันใดอันหนึ่งด้านล่างเพื่อลด CLS:
          // width={1600} height={900}
          // style={{ aspectRatio: "16/9" }}
        />
      );
    }

    return (
      <div
        className="relative w-full overflow-hidden bg-gray-200"
        style={{ aspectRatio: FIT_RATIO, maxHeight: FIT_MAX_H }}
      >
        {/* พื้นหลังเบลอ — เห็นเฉพาะตอนกรอบกว้างกว่าภาพ (จอ tablet แนวนอน / PC) */}
        <img
          aria-hidden="true"
          alt=""
          src={banner.image}
          loading={isFirst ? "eager" : "lazy"}
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-80 pointer-events-none select-none"
        />
        <img
          className="relative w-full h-full object-contain"
          src={banner.image}
          alt={alt}
          loading={isFirst ? "eager" : "lazy"}
          fetchpriority={isFirst ? "high" : "auto"}
          decoding="async"
        />
      </div>
    );
  };

  return (
    <div
      className={isFit ? "overflow-hidden mx-auto" : "w-full overflow-hidden"}
      style={isFit ? { width: fitWidth, maxWidth: "100%" } : undefined}
    >
      {loading ? (
        <LoadingBar loading={true} label="Loading..." />
      ) : banners.length === 0 ? (
        <></>
      ) : (
        <div className="relative w-full flex justify-center overflow-hidden">
          <div
            className="flex transition-transform duration-500 w-full"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {banners.map((banner, index) => {
              return (
                <div
                  key={index}
                  className="w-full flex-shrink-0 flex justify-center"
                >
                  <a
                    href={banner.targetURI}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex justify-center"
                    onClick={() => {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "hero_banner_click",
                        banner_name: banner.name || `Banner ${index + 1}`,
                        banner_target: banner.targetURI,
                        banner_index: index,
                      });
                    }}
                  >
                    {renderImage(banner, index)}
                  </a>
                </div>
              );
            })}
          </div>
          {/* Pagination Dots */}
          <div className="absolute bottom-3 flex space-x-2 z-10">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentIndex === index ? "bg-white scale-110" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerSection;
